import { Operation } from 'fast-json-patch';
import { Coverage, Organization, Patient, RelatedPerson } from 'fhir/r4b';
import { getPayerId } from 'utils/lib/helpers/helpers';
import {
  eligibilityRequirementKeys,
  InsurancePlanDTO,
  InsurancePlanRequirementKeyBooleans,
} from 'utils/lib/types/data/telemed/insurances.types';
import { create } from 'zustand';

export interface Insurance {
  coverage: Coverage;
  relatedPerson: RelatedPerson;
  isTemp: true;
}

interface ResourcePatches {
  patient: Operation[];
  coverages: { [id: string]: Operation[] }; // key is Coverage.id
  relatedPersons: { [id: string]: Operation[] }; // key is RelatedPerson.id
}

type PatientState = {
  patient: Patient | null;
  insurances: Coverage[];
  policyHolders: RelatedPerson[];
  patchOperations: ResourcePatches;
  tempInsurances: Insurance[];
};
interface PatientStoreActions {
  setPatient: (patient: Patient | null) => void;
  reset: () => void;
}

const PATIENT_INITIAL: PatientState = {
  patient: null,
  insurances: [],
  policyHolders: [],
  tempInsurances: [],
  patchOperations: {
    patient: [],
    coverages: {},
    relatedPersons: {},
  },
};

export const usePatientStore = create<PatientState & PatientStoreActions>()((set) => ({
  ...PATIENT_INITIAL,
  setPatient: (patient) => set({ patient }),
  reset: () => {
    set({
      patchOperations: PATIENT_INITIAL.patchOperations,
    });
  },
}));

export const createInsurancePlanDto = (organization: Organization): InsurancePlanDTO => {
  const { id, name, partOf } = organization;

  if (!id || !name) {
    throw new Error('Insurance is missing id, name or owning organization.');
  }

  const payerId = getPayerId(organization);

  if (!payerId) {
    throw new Error('Owning organization is missing payer-id.');
  }

  const insurancePlanDto: InsurancePlanDTO = {
    id,
    name,
    ownedBy: partOf,
    payerId,
    ...(Object.fromEntries(
      eligibilityRequirementKeys.map((key) => [key, false])
    ) as InsurancePlanRequirementKeyBooleans),
  };

  return insurancePlanDto;
};
